<template>
  <AppSidebar>
    <div class="space-y-10">
      <!-- Dashboard Stats -->
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
        <div v-for="stat in dashboardStats" :key="stat.title" class="bg-white dark:bg-gray-800 rounded-3xl border border-gray-100 dark:border-gray-700 shadow-sm hover:shadow-md transition-all group p-6">
          <div class="flex items-center justify-between mb-4">
            <div class="w-12 h-12 bg-primary-50 dark:bg-primary-900/30 rounded-2xl flex items-center justify-center group-hover:scale-105 transition-transform">
              <component :is="stat.icon" class="w-6 h-6 text-primary-600 dark:text-primary-400" />
            </div>
            <div class="text-right">
              <span :class="stat.trend === 'up' ? 'text-green-600 bg-green-50 dark:bg-green-900/20' : 'text-red-600 bg-red-50 dark:bg-red-900/20'" class="text-xs font-bold px-2.5 py-1 rounded-full border border-current opacity-80">
                {{ stat.change }}
              </span>
            </div>
          </div>
          <div>
            <p class="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-1">{{ stat.title }}</p>
            <p class="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">{{ stat.value }}</p>
          </div>
        </div>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-2 gap-10">
        <!-- Recent Orders -->
        <div class="bg-white dark:bg-gray-800 rounded-3xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
          <div class="p-6 border-b border-gray-50 dark:border-gray-700/50 flex items-center justify-between">
            <div>
              <h2 class="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-widest" style="font-family: Poppins, sans-serif">Recent Transactions</h2>
              <p class="text-xs text-gray-500 dark:text-gray-400 font-medium">Latest activity from your clients</p>
            </div>
            <router-link to="/dashboard/admin/orders" class="text-xs font-bold text-primary-600 dark:text-primary-400 hover:underline uppercase tracking-widest">View Portfolio</router-link>
          </div>
          <div class="p-6">
            <div v-if="recentOrders.length === 0" class="text-center py-10">
              <div class="w-12 h-12 bg-gray-50 dark:bg-gray-900/50 rounded-full flex items-center justify-center mx-auto mb-3 border border-gray-100 dark:border-gray-800">
                <OrderIcon class="w-6 h-6 text-gray-300 dark:text-gray-700" />
              </div>
              <p class="text-xs text-gray-400 dark:text-gray-600 font-medium">No recent orders found</p>
            </div>
            <div v-else class="space-y-4">
              <div v-for="order in recentOrders" :key="order.id" class="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-900/20 rounded-2xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                <div class="flex-1 min-w-0 mr-4">
                  <div class="flex items-center gap-2 mb-1">
                    <span class="text-[10px] font-bold text-primary-600 bg-primary-50 dark:bg-primary-900/30 px-1.5 py-0.5 rounded border border-primary-100 dark:border-primary-800">{{ order.order_number }}</span>
                    <span class="text-sm font-semibold text-gray-900 dark:text-white truncate">{{ order.client_name || 'Client' }}</span>
                  </div>
                  <p class="text-[11px] text-gray-500 dark:text-gray-400">{{ formatDate(order.created_at) }}</p>
                </div>
                <div class="text-right">
                  <span class="inline-flex px-2 py-0.5 text-[10px] font-bold rounded-full border mb-1" :class="getStatusClass(order.status)">
                    {{ formatStatus(order.status) }}
                  </span>
                  <p class="text-sm font-bold text-gray-900 dark:text-white">
                    ${{ order.total_amount?.toLocaleString() }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Quick Actions -->
        <div class="bg-white dark:bg-gray-800 rounded-3xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
          <div class="p-6 border-b border-gray-50 dark:border-gray-700/50">
            <h2 class="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-widest" style="font-family: Poppins, sans-serif">Quick Actions</h2>
            <p class="text-xs text-gray-500 dark:text-gray-400 font-medium">Common management tasks</p>
          </div>
          <div class="p-6">
            <div class="grid grid-cols-1 gap-4">
              <router-link to="/dashboard/admin/services" class="flex items-center p-4 bg-gray-50 dark:bg-gray-900/20 rounded-2xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors group">
                <div class="w-12 h-12 bg-green-100 dark:bg-green-900/20 rounded-xl flex items-center justify-center mr-4 group-hover:scale-105 transition-transform">
                  <ServicesIcon class="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p class="text-sm font-bold text-gray-900 dark:text-white">Manage Services</p>
                  <p class="text-xs text-gray-500 dark:text-gray-400">Update services and pricing</p>
                </div>
              </router-link>

              <router-link to="/dashboard/admin/content/dev-profiles" class="flex items-center p-4 bg-gray-50 dark:bg-gray-900/20 rounded-2xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors group">
                <div class="w-12 h-12 bg-orange-100 dark:bg-orange-900/20 rounded-xl flex items-center justify-center mr-4 group-hover:scale-105 transition-transform">
                  <UsersIcon class="w-6 h-6 text-orange-600 dark:text-orange-400" />
                </div>
                <div>
                  <p class="text-sm font-bold text-gray-900 dark:text-white">Expert Directory</p>
                  <p class="text-xs text-gray-500 dark:text-gray-400">Manage developer specializations</p>
                </div>
              </router-link>
            </div>
          </div>
        </div>
      </div>

      <!-- Recent Activity -->
      <div class="bg-white dark:bg-gray-800 rounded-3xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
        <div class="p-6 border-b border-gray-50 dark:border-gray-700/50">
          <h2 class="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-widest" style="font-family: Poppins, sans-serif">System Audit Feed</h2>
          <p class="text-xs text-gray-500 dark:text-gray-400 font-medium">Chronological history of recent actions</p>
        </div>
        <div class="p-6">
          <div v-if="recentActivity.length === 0" class="text-center py-10">
            <p class="text-xs text-gray-500 dark:text-gray-400 italic">No activity logs recorded yet</p>
          </div>
          <div v-else class="space-y-6">
            <div v-for="activity in recentActivity" :key="activity.id" class="flex items-start gap-4">
              <div class="w-8 h-8 bg-gray-50 dark:bg-gray-900 rounded-xl flex items-center justify-center flex-shrink-0 border border-gray-100 dark:border-gray-800">
                <component :is="activity.icon" class="w-4 h-4 text-gray-500 dark:text-gray-400" />
              </div>
              <div class="flex-1 pb-4 border-b border-gray-50 dark:border-gray-800/50 last:border-0 min-w-0">
                <p class="text-xs font-semibold text-gray-800 dark:text-gray-200 mb-1 leading-relaxed">{{ activity.message }}</p>
                <p class="text-[10px] text-gray-400 dark:text-gray-500">{{ formatDate(activity.timestamp) }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </AppSidebar>
</template>

<script setup>
import { computed, onMounted } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useDashboardStore } from '@/stores/dashboard'
import AppSidebar from '@/components/layout/AppSidebar.vue'

const authStore = useAuthStore()
const dashboardStore = useDashboardStore()

// Data from store
const recentOrders = computed(() => dashboardStore.recentOrders)
const recentActivity = computed(() => dashboardStore.recentActivity)
const dashboardStats = computed(() => dashboardStore.stats)

// Helper functions
const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

const formatStatus = (status) => {
  return status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())
}

const getStatusClass = (status) => {
  const classes = {
    'pending_review': 'bg-yellow-100 text-yellow-800',
    'quote_sent': 'bg-blue-100 text-blue-800',
    'approved': 'bg-green-100 text-green-800',
    'in_progress': 'bg-purple-100 text-purple-800',
    'review': 'bg-orange-100 text-orange-800',
    'completed': 'bg-green-100 text-green-800',
    'cancelled': 'bg-red-100 text-red-800'
  }
  return classes[status] || 'bg-gray-100 text-gray-800'
}

onMounted(() => {
  dashboardStore.fetchStats()
})
</script>

<script>
// Icon components
export default {
  components: {
    OrdersIcon: {
      template: `
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"></path>
        </svg>
      `
    },
    UsersIcon: {
      template: `
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"></path>
        </svg>
      `
    },
    RevenueIcon: {
      template: `
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"></path>
        </svg>
      `
    },
    ProjectsIcon: {
      template: `
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
        </svg>
      `
    },
    CompletedIcon: {
      template: `
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
      `
    },
    ContentIcon: {
      template: `
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
        </svg>
      `
    },
    ServicesIcon: {
      template: `
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
        </svg>
      `
    },
    OrderIcon: {
      template: `
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"></path>
        </svg>
      `
    },
    ProfileIcon: {
      template: `
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
        </svg>
      `
    },
    ProductsIcon: {
      template: `
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path>
        </svg>
      `
    },
    LayoutIcon: {
      template: `
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"></path>
        </svg>
      `
    },
    ClockIcon: {
      template: `
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
      `
    }
  }
}
</script>
